import { useCallback, useMemo } from 'react'
import type { ApolloError } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { useToast } from '@winnin/winnin_ui.components.toast'

import type { GetBrandTerritoryQuery } from 'generated/graphql'
import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { CONTENT_TYPE_BY_TERRITORY_KEY } from 'modules/brands/common/brands.consts'
import type { TBrandContentType, IBrandData } from 'modules/brands/common/brands.types'
import { getCountryDisplayName } from 'modules/brands/common/brands.utils'
import { useBrandTerritories } from 'modules/brands/common/hooks/use-brand-territories'
import { countries as countriesUtils, datadog, TOLGEE_GLOBALS } from 'utils'
import { jsonUtils } from 'utils/jsonUtils'

import { GET_BRAND_QUERY_ERROR_LOG } from './use-brand-query.consts'
import type { IUseBrandQueryProps, ITerritoryResponse } from './use-brand-query.types'

export const useBrandQuery = ({ territoryId }: IUseBrandQueryProps) => {
  const t = useTranslate()
  const history = useHistory()
  const currentLanguage = useCurrentLanguage()
  const { createToast } = useToast()

  const handleQueryCompleted = useCallback(
    (data: GetBrandTerritoryQuery) => {
      const territory = data?.getTerritory as ITerritoryResponse | undefined

      if (territory?.success === false) {
        history.push('/')

        if (territory?.message !== 'Access denied') {
          createToast({ content: t(TOLGEE_GLOBALS.toastErrorMessage) })
        }
      }
    },
    [history, createToast, t]
  )

  const handleQueryError = useCallback(
    (error: ApolloError) => {
      datadog.dispatchRequestErrorLog({
        log: GET_BRAND_QUERY_ERROR_LOG,
        query: { name: 'getBrandTerritory', variables: { territoryId } },
        error,
      })
      history.push('/')
      createToast({ content: t(TOLGEE_GLOBALS.toastErrorMessage) })
    },
    [history, createToast, t, territoryId]
  )

  const { data, loading, error, currentBrand, refetch } = useBrandTerritories({
    currentTerritoryId: territoryId,
    brandContentType: 'total',
    onCompleted: handleQueryCompleted,
    onError: handleQueryError,
  })

  const selectedContentType = useMemo((): TBrandContentType => {
    if (!currentBrand?.workspaceBrandTerritories || !data?.getTerritory) return 'total'

    const urlTerritoryId = data.getTerritory.territoryId
    const territories = currentBrand.workspaceBrandTerritories

    const territoryKey = Object.keys(CONTENT_TYPE_BY_TERRITORY_KEY).find(
      key => territories[key as keyof typeof territories]?.territoryId === urlTerritoryId
    )

    return territoryKey ? CONTENT_TYPE_BY_TERRITORY_KEY[territoryKey] : 'total'
  }, [currentBrand, data])

  const brandData: IBrandData | null = useMemo(() => {
    if (!data?.getTerritory) return null

    const countryCode = data?.getWorkspace?.myBrandSoA?.category?.country
    const category = countryCode ? countriesUtils.byKeyValue('ALPHA_2', countryCode)?.NAME : undefined

    const territory = data.getTerritory
    const brand = 'brand' in territory ? territory.brand : undefined

    return {
      name: brand?.name ?? territory.name,
      territoryId: territory.territoryId,
      type: 'type' in territory ? territory.type : undefined,
      logo: brand?.thumbnailUrl ?? currentBrand?.thumbnail,
      category,
      brandColor: brand?.color ?? jsonUtils.parse(currentBrand?.customSettings)?.color ?? null,
      isVerified: brand?.isVerified ?? false,
      country: getCountryDisplayName(brand?.country, currentLanguage) ?? undefined,
    }
  }, [data, currentBrand, currentLanguage])

  return {
    loading,
    error,
    brandData,
    currentBrand,
    selectedContentType,
    refetch,
  }
}
